import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { isSameDay } from 'date-fns';

import { UscStoreService } from '../../../services/usc-store/usc-store.service';
import { CalendarService, SessionService } from '../../../services';
import { UscStoreSchedulingService } from '../../../services/usc-store-scheduling/usc-store-scheduling-service';

@Component({
    selector: 'sn-usc-dialog',
    templateUrl: './usc-dialog.component.html',
    styleUrls: ['./usc-dialog.component.css']
})
export class UscDialogComponent implements OnInit {
    @Input() data: any;    // this is the view item from the list view which may not contain all properties needed for this modal
    @Input() parentView: string;
    @Input() dlgType: string;
    @Input() msg_config: any;
    @Input() quick_sale: any;

    @ViewChild('sn_dialog') sn_dialog: ElementRef;

    public newAssociate: string;
    public churn_resolution: string;
    public safe_html: SafeHtml;
    public show_process_icon: boolean = true;
    public queue_position_offset: number = 0;
    private adjusted_queue_position_offset: number = 0;
    public appointment_confirmation_number: string;
    public spanish_requested: boolean = false;
    public doc_approval_rejection: string;

    constructor(
        public activeModal: NgbActiveModal,
        public uscStoreService: UscStoreService,
        public sessionService: SessionService,
        public calendarService: CalendarService,
        public uscStoreSchedulingService: UscStoreSchedulingService
    ) { }

    ngOnInit() {
        if (this.msg_config && this.msg_config.show_process_icon != null) this.show_process_icon = this.msg_config.show_process_icon;
        if (this.dlgType == this.uscStoreService.dialog_type_custom_msg) {
            if (this.msg_config && this.msg_config.timeout > 0) {
                setTimeout(() => {
                    this.activeModal.close();
                }, this.msg_config.timeout);
            }
        } else if (this.dlgType == this.uscStoreService.dialog_type_welcome_message) {
            this.safe_html = this.uscStoreService.getSalesNOWSanitizedHtml(this.data.msg);
        }
    }
        
    adjustDialogStyle() {
        var modal:any = document.getElementsByClassName('modal-dialog');
        // HACK: force to vertically center the dialog
        if (modal && this.dlgType == this.uscStoreSchedulingService.dialog_type_cancel_appt_confirmation) {
            modal[0].style.display = 'flex';
            modal[0].style.alignItems  = 'center';
            modal[0].style.minHeight = 'calc(100% - 72px)';
            modal[0].style.width = '528px';
        }
    }

    ngAfterViewInit() {
        this.trapFocus(this.sn_dialog.nativeElement);
        setTimeout(() => { this.adjustDialogStyle(); }, 0);
    }    

    trapFocus(element) {
        const focusableEls1 = element.querySelectorAll(
            'a[href], button, textarea, input[type="text"],' +
            'input[type="radio"], input[type="checkbox"], select'
        );
        const focusableEls = Array.from(focusableEls1)
            .filter((el: any) => !el.disabled);
        const firstFocusableEl: any = focusableEls[0];
        const lastFocusableEl: any = focusableEls[focusableEls.length - 1];

        element.addEventListener('keydown', function (e) {
            var isTabPressed = e.keyCode === 9; // isTabPressed
            if (!isTabPressed) return;

            if (e.shiftKey) /* shift + tab */ {
                if (document.activeElement === firstFocusableEl) {
                    lastFocusableEl.focus();
                    e.preventDefault();
                }
            } else /* tab */ {
                if (document.activeElement === lastFocusableEl) {
                    firstFocusableEl.focus();
                    e.preventDefault();
                }
            }
        });
    }

    onSubmit(response) {
        if (response == this.uscStoreService.response_edit_associate) {
            this.activeModal.close({ response: response, associate: this.newAssociate });
        } else if (response == this.uscStoreService.response_edit_churn_resolution) {
            this.activeModal.close({ response: response, churn_resolution: this.churn_resolution });
        } else if (response == this.uscStoreService.response_adjust_queue_position) {
            this.activeModal.close({ response: response, position_offset: this.adjusted_queue_position_offset });
        } else if (response == this.uscStoreService.response_svt_appointment_check_in) {
            this.activeModal.close({ response: response, confirmation_number: this.appointment_confirmation_number, spanish_requested: this.spanish_requested ? 'Yes' : 'No' });
        } else if (response == this.uscStoreService.response_document_approval_rejected) {
            this.activeModal.close({ response: response, rejection: this.doc_approval_rejection });
        } else {
            this.activeModal.close({ response: response });
        }
    }

    onCancel(response) {
        this.activeModal.dismiss(response);
    }

    isDlgType(type) {
        return this.dlgType == type;
    }

    isLanguagePreference(lang) {
        var bValid = false;
        // if language preference is defined
        if (this.data && this.data.cfd_lp && this.data.cfd_lp.length > 0) {
            bValid = (this.data.cfd_lp == lang);
        } else if (lang == "English") {
            bValid = true;
        }
        return bValid;
    }

    showClose(): boolean {
        switch (this.dlgType) {
            case this.uscStoreService.dialog_type_language_preference:
                return true;
            case this.uscStoreService.dialog_type_optin_request:
                return !this.isOptInRequestAllowed();
            case this.uscStoreService.dialog_type_svt_adjust_queue_position:
                return true;
        }
        return false;
    }

    isLanguageEnabled(language) {
        switch (language) {
            case 'Spanish': return this.data.cfd_hsi != 'Yes';
        }
        return true;
    }

    isMultiLanguageEnabled(): boolean {
        var count = 1;
        if (this.uscStoreService.isMultiLanguageEnabled()) {
            if (this.data.cfd_hsi != 'Yes') count++;
        }
        return count > 1;
    }

    isSmsIntroductionEnabled(): boolean {
        return this.data.co_type.indexOf('Existing') >= 0 && this.data.cfd_esi == "Yes";
    }

    isDailySmsIntroductionLimitExceeded(): boolean {
        return this.isSmsIntroductionEnabled() && this.uscStoreService.isTextFirstSMSIntroLimitExceeded(this.data);
    }

    isCampaignIntroSmsLimitExceeded(): boolean {
        //return true;
        return this.isSmsIntroductionEnabled() && this.uscStoreService.isCampaignIntroLimitExceeded(this.data.cm_ID, this.data.cfd_esil, this.data.cfd_sms_limit);
    }

    isCallOutcomeRequired() {
        return !this.isDailySmsIntroductionLimitExceeded() && !this.isSmsIntroductionTimeLimitExceeded() && !this.isCampaignIntroSmsLimitExceeded() && !this.isOptInRequestAllowed();
    }

    isSmsIntroductionTimeLimitExceeded() {
        return this.uscStoreService.isSmsIntroTimeLimitExceeded(this.data, new Date());
    }

    isOptInRequestAllowed(): boolean {
        if (this.isDailySmsIntroductionLimitExceeded() || this.isSmsIntroductionTimeLimitExceeded() || this.isCampaignIntroSmsLimitExceeded()) return false;
        switch (this.data.cm_name) {
            case 'Walk-In':     // Corp/Agent Retail
            case 'Associate':   // Inside Sales
                return true;
            default:
                // allow if,
                // 1) in Middle/Lasting Impression
                if (this.data.dl_type != 'First Impression') return true;
                // 2a) is inside-sales and an outcome was recorded
                if (this.uscStoreService.isUSCellInsideSales() && this.data.cfd_lcod) return true;
                // 2b) is telesales and an outcome was recorded
                if (this.uscStoreService.isUSCellTelesales() && this.data.cfd_lcod) return true;
                // 3) campaign has 'Allow Texting Without Call' = 'Yes'
                if (this.data.cfd_atwc == "Yes") return true;
                // 4) if phone number is hidden, just allow texting
                if (this.data.cfd_hpn == 'Yes') return true;
                // 5) if an outcome was record today
                if (this.data.cfd_lcod) {
                    var today = new Date();
                    var last_call_outcome_date = new Date(this.data.cfd_lcod);
                    return isSameDay(today, last_call_outcome_date);
                }
                break;
        }
        return false;
    }

    //#region dialog_type_quick_sale_details 
    isQuickSaleCreation() { this.parentView == this.uscStoreService.form_quick_sale; }
    isSaleTypeValid() { return this.data.sale_type && this.data.sales_figures; }
    getCurrentSalesFigure(figure) { return this.data.sales_figures[figure] ? this.data.sales_figures[figure] : 0; }
    getNewSalesFigure(figure) {
        var sale_figure_mappings = {
            "postpaid_basic": "dl_postpaid_basic",
            "postpaid_everyday": "dl_postpaid_everyday",
            "postpaid_even_better": "dl_postpaid_even_better",
            "postpaid": "dl_estimated_units",
            "prepaid": "dl_prepaid",
            "cd": "dl_connected_device",
            "hsi": "dl_hsi",
            "upgrades_basic": "dl_upgrades_basic",
            "upgrades_everyday": "dl_upgrades_everyday",
            "upgrades_even_better": "dl_upgrades_even_better",
            "upgrades": "dl_estimated_value",
            "bir": "dl_bir",
            "accessories_revenue": "dl_estimated_value3",
            "high_msrp_accessories": "dl_estimated_units3",
            "device_trade_ins": "cfd_dti",
            "everyday_plans": "dl_estimated_value4",
            "even_better_plans": "dl_estimated_units5",
            "dp_basic": "cfd_dpb",
            "dp_standard": "cfd_dps",
            "dp_advanced": "cfd_dpa",
            "dp_advanced_acs": "cfd_dpacs",
            "spiff": "cfd_spiff",
        }

        var sale_figure = 0;
        this.quick_sale.forEach(f => {
            if (f["key"] == sale_figure_mappings[figure]) sale_figure = f["value"];
        })

        return sale_figure ? sale_figure : 0;
    }
    //#endregion

    //#region dialog_type_appointment_assignment 
    formatContactNumber(phone: string) {
        var formatted = "";
        if (phone && phone.length == 10) {
            formatted = phone.substring(0, 3) + '-' + phone.substring(3, 6) + '-' + phone.substring(6, 10);
        }
        return formatted;
    }

    getEventTime(date) {
        var start = new Date(date);
        var hours = start.getHours();
        var minutes = (start.getMinutes() < 10 ? '0' : '') + start.getMinutes().toString();
        var ampm = (hours >= 12) ? "PM" : "AM";
        if (hours > 12) hours = hours - 12;
        return `${hours}:${minutes} ${ampm}`;
    }

    getWelcomeMessageTitle() {
        return "Welcome Message";
    }

    getWelcomeMessage() {
        return this.data.msg;
    }
    //#endregion

    //#region
    getUserAwards() {
        var folder = "../../../../assets/img/usc-store/awards/";
        var filename = (this.msg_config.current_awards + " Award.jpg").replace(/ /g, "_");
        return this.msg_config && this.msg_config.current_awards ? folder + filename : "";
    }
    //#endregion

    getViewStyle() {
        var style: any = {}
        var vs = this.sessionService.getQueryVariable("vs");
        if (vs) style = JSON.parse(vs);
        return style;
    }

    moveQueuePositionOffset(direction) {
        this.adjusted_queue_position_offset = this.queue_position_offset * direction;
        this.onSubmit(this.uscStoreService.response_adjust_queue_position);
    }

    getSvtAppointmentTime() {
        return this.calendarService.getDateText(new Date(Date.parse(this.data.appointment_time)), 'hh:mm tt'); 
    }

    allowApptCheckIn() {
        return this.appointment_confirmation_number && (parseInt(this.appointment_confirmation_number) > 1000000 || this.appointment_confirmation_number.length >= 10);
    }

    getAriaLabelledBy() { 
        if (this.isDlgType(this.uscStoreSchedulingService.dialog_type_cancel_appt_confirmation)) return "txtApptConfirm txtApptDesc";
        return "";
    }

    getAriaDescribedBy() { 
        return "";
    }

    isSpanishAssociateAvailable() {
        return this.uscStoreService.isSpanishAssociateAvailable();
    }

    toggleSpanishRequested() {
        this.spanish_requested = this.spanish_requested ? false : true;
    }
}



// WEBPACK FOOTER //
// C:/Azure/Source/Workspaces/SalesNOW/SalesSOLO/WebSites/SalesSOLO.Web.Angular.telesales/src/app/components/usc-store/usc-dialog/usc-dialog.component.ts